/**
 * Urlopy (NX-421): przegląd zespołu, wnioski z kolizjami, ręczny wymiar i powiązania z InsERT. Zakres danych
 * i uprawnienia egzekwuje backend (`/leave/*`); decyzja o wniosku idzie przez `/planner/requests/:id/respond`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LEAVE_API,
  type LeaveEmployeeDto,
  type LeaveOverviewDto,
  type LeaveRequestRow,
  type LeaveRequestsQuery,
  type PlannerTeamMember,
  type ScheduleRequestDto,
  type VacationBalanceDto,
  type VacationEntitlementInput,
} from '@nexops/shared';
import { plannerKeys, plannerRoutes } from '@/features/planner/api/routes';
import { api } from '@/shared/lib/api';

export const leaveKeys = {
  all: ['leave'] as const,
  overview: (year: number) => [...leaveKeys.all, 'overview', year] as const,
  requests: (q: LeaveRequestsQuery) => [...leaveKeys.all, 'requests', q] as const,
  pending: () => [...leaveKeys.all, 'pending-count'] as const,
  employees: () => [...leaveKeys.all, 'employees'] as const,
};

export function useLeaveOverview(year: number) {
  return useQuery({ queryKey: leaveKeys.overview(year), queryFn: () => api.get<LeaveOverviewDto>(LEAVE_API.overview, { year }), staleTime: 30_000 });
}

export function useLeaveRequests(q: LeaveRequestsQuery, enabled = true) {
  return useQuery({
    queryKey: leaveKeys.requests(q),
    queryFn: () => api.get<LeaveRequestRow[]>(LEAVE_API.requests, { ...q, toDecide: q.toDecide ? 'true' : undefined }),
    enabled,
  });
}

/** Licznik oczekujących do mojej decyzji (menu, zakładka Wnioski). */
export function useLeavePendingCount(enabled: boolean) {
  return useQuery({
    queryKey: leaveKeys.pending(),
    queryFn: () => api.get<{ count: number }>(LEAVE_API.pendingCount),
    enabled,
    refetchInterval: 60_000,
    retry: false,
    meta: { silent: true },
  });
}

export function useLeaveEmployees(enabled: boolean) {
  return useQuery({ queryKey: leaveKeys.employees(), queryFn: () => api.get<LeaveEmployeeDto[]>(LEAVE_API.employees), enabled });
}

/** Konta do powiązania (aktywni w zakresie planera — admin / kadry z zakresem ALL widzą organizację). */
export function useLinkableUsers(enabled: boolean) {
  return useQuery({ queryKey: plannerKeys.team(), queryFn: () => api.get<PlannerTeamMember[]>(plannerRoutes.team), enabled, staleTime: 60_000 });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: leaveKeys.all });
    void qc.invalidateQueries({ queryKey: plannerKeys.all });
  };
}

export function useSaveLeaveEntitlement() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: ({ userId, input }: { userId: string; input: VacationEntitlementInput }) => api.put<VacationBalanceDto>(LEAVE_API.entitlement(userId), input),
    onSuccess: done,
  });
}

export function useClearLeaveEntitlement() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: ({ userId, year }: { userId: string; year: number }) => api.delete<VacationBalanceDto>(`${LEAVE_API.entitlement(userId)}?year=${year}`),
    onSuccess: done,
  });
}

export function useLinkEmployee() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string | null }) =>
      userId ? api.put<LeaveEmployeeDto>(LEAVE_API.link(id), { userId }) : api.delete<LeaveEmployeeDto>(LEAVE_API.link(id)),
    onSuccess: done,
  });
}

export function useRespondLeaveRequest() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: ({ id, status, response }: { id: string; status: 'APPROVED' | 'REJECTED'; response: string | null }) =>
      api.patch<ScheduleRequestDto>(plannerRoutes.requestRespond(id), { status, response }),
    onSuccess: done,
  });
}
